import { useMemo, type CSSProperties, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import type { FleetMetricAggregate, FleetMetricsSeries } from '@immo/api-contract';
import { Card, Skeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@immo/ui-kit';
import { useTranslations } from '@/i18n/use-translations';
import { Approx, ClassBar, ClassLegend, ClassSwatch, Eyebrow, MONO_STYLE, useMetricFormat } from './class-bar';
import {
  ARIA_SORT,
  SortableHeader,
  useTableSort,
  type SortState,
  type SortStateHandle,
  type SortValue,
} from '@/components/sortable-table';
import { MetricCard, type SeriesRow } from './metric-panels';
import {
  classValue,
  delta,
  type Delta,
  isApproximate,
  isUnknown,
  parsedRuns,
  type ClassChoice,
  type MetricsView,
} from './metrics-math';
import { FLEET_PRICING, TOKEN_CLASS_LABEL, TOKEN_CLASSES, tokensByClass, type TokenClass } from './pricing';

const HEADER_STYLE: CSSProperties = {
  fontSize: 'var(--text-xs)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  whiteSpace: 'nowrap',
};

const CLASS_HEADER_STYLE: CSSProperties = {
  ...HEADER_STYLE,
  textTransform: 'none',
  letterSpacing: 0,
  fontFamily: 'var(--font-mono)',
};

const RIGHT: CSSProperties = { textAlign: 'right' };

function Mono({
  children,
  size = 'var(--text-base-sm)',
  color = 'var(--text-primary)',
  weight,
}: {
  readonly children: ReactNode;
  readonly size?: string;
  readonly color?: string;
  readonly weight?: string;
}) {
  return <span style={{ ...MONO_STYLE, fontSize: size, color, fontWeight: weight }}>{children}</span>;
}

function Kpi({
  label,
  value,
  approximate,
  small,
  change,
  sub,
  testId,
  children,
}: {
  readonly label: string;
  readonly value: string;
  readonly approximate: boolean;
  readonly small?: boolean;
  readonly change?: string | null;
  readonly sub?: string | null;
  readonly testId: string;
  readonly children?: ReactNode;
}) {
  const t = useTranslations('pages.admin.fleet-metrics');

  return (
    <div
      data-testid={testId}
      className="flex min-w-0 flex-col"
      style={{ gap: 'var(--space-1)', padding: 'var(--space-4) var(--space-5)' }}
    >
      <Eyebrow>{label}</Eyebrow>
      <div className="flex min-w-0 items-baseline" style={{ gap: 'var(--space-1-5)' }}>
        <Approx show={approximate} title={t('approx-title')} />
        <span
          data-testid={`${testId}-value`}
          style={{
            ...MONO_STYLE,
            fontSize: small ? 'var(--text-lg)' : 'var(--text-2xl)',
            fontWeight: small ? 'var(--font-weight-medium)' : 'var(--font-weight-semibold)',
            color: small ? 'var(--text-secondary)' : 'var(--text-primary)',
            letterSpacing: '-0.02em',
          }}
        >
          {value}
        </span>
      </div>
      {change && <span className="text-sm text-text-secondary">{change}</span>}
      {sub && (
        <span className="text-sm text-text-tertiary" style={{ lineHeight: 1.4 }}>
          {sub}
        </span>
      )}
      {children}
    </div>
  );
}

/** Tokens (with their four classes) · runs · tokens per run · runtime · cost — cost the smallest. */
export function KpiRow({
  total,
  previous,
  choice,
  cost,
  previousLabel,
  compact,
}: {
  readonly total: FleetMetricAggregate;
  readonly previous: FleetMetricAggregate | null;
  readonly choice: ClassChoice;
  readonly cost: number;
  readonly previousLabel: string;
  readonly compact: boolean;
}) {
  const t = useTranslations('pages.admin.fleet-metrics');
  const format = useMetricFormat();
  const value = classValue(total, choice);
  const parsed = parsedRuns(total);
  const approximate = isApproximate(total);
  const change = (current: number, before: number) => `${format.delta(delta(current, before))} ${previousLabel}`;
  const noPrevious = previous === null ? t('no-previous') : null;
  const runsSub = [
    noPrevious,
    total.unparsedRuns > 0
      ? t('kpi-unparsed', { count: total.unparsedRuns })
      : total.runs > 0
        ? t('kpi-all-readable')
        : null,
  ]
    .filter((entry) => entry !== null)
    .join(' · ');
  const tokens = tokensByClass(total);

  const tiles = [
    <Kpi
      key="tokens"
      testId="kpi-tokens"
      label={choice === 'all' ? t('kpi-tokens-all') : t('kpi-tokens-class', { class: TOKEN_CLASS_LABEL[choice] })}
      value={format.tokensShort(value)}
      approximate={approximate}
      change={previous === null ? null : change(value, classValue(previous, choice))}
      sub={noPrevious}
    >
      {classValue(total, 'all') > 0 && (
        <div className="flex flex-col" style={{ marginTop: 'var(--space-2)', gap: 'var(--space-1-5)' }}>
          <ClassBar values={tokens} height={10} labels={false} />
          <ClassLegend values={tokens} />
        </div>
      )}
    </Kpi>,
    <Kpi
      key="runs"
      testId="kpi-runs"
      label={t('kpi-runs')}
      value={format.count(total.runs)}
      approximate={false}
      change={previous === null ? null : change(total.runs, previous.runs)}
      sub={runsSub}
    />,
    <Kpi
      key="per-run"
      testId="kpi-per-run"
      label={t('kpi-per-run')}
      value={parsed > 0 ? format.tokensShort(Math.round(value / parsed)) : '—'}
      approximate={false}
      sub={
        parsed > 0 ? t('kpi-output-per-run', { value: format.tokensShort(Math.round(tokens.output / parsed)) }) : null
      }
    />,
    <Kpi
      key="runtime"
      testId="kpi-runtime"
      label={t('kpi-runtime')}
      value={format.duration(total.durationMs)}
      approximate={false}
      sub={
        total.runs > 0
          ? t('kpi-runtime-per-run', { value: format.duration(Math.round(total.durationMs / total.runs)) })
          : null
      }
    />,
    <Kpi
      key="cost"
      testId="kpi-cost"
      small
      label={t('kpi-cost')}
      value={format.eur(cost)}
      approximate={approximate}
      sub={t('kpi-cost-sub')}
    />,
  ];

  if (compact) {
    return (
      <Card className="w-full min-w-0 overflow-hidden" data-testid="kpi-row">
        {tiles[0]}
        <div className="grid grid-cols-2" style={{ borderTop: '1px solid var(--color-neutral-200)' }}>
          {tiles.slice(1).map((tile, index) => (
            <div
              key={index}
              className="min-w-0"
              style={{
                borderRight: index % 2 === 0 ? '1px solid var(--color-neutral-200)' : 'none',
                borderTop: index > 1 ? '1px solid var(--color-neutral-200)' : 'none',
              }}
            >
              {tile}
            </div>
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card className="grid min-w-0" data-testid="kpi-row" style={{ gridTemplateColumns: '2.1fr 1fr 1fr 1fr 1fr' }}>
      {tiles.map((tile, index) => (
        <div
          key={index}
          className="min-w-0"
          style={{ borderLeft: index > 0 ? '1px solid var(--color-neutral-200)' : 'none' }}
        >
          {tile}
        </div>
      ))}
    </Card>
  );
}

const HEAT_STEPS = [
  'var(--color-info-100)',
  'var(--color-info-400)',
  'var(--color-info-500)',
  'var(--color-info-700)',
] as const;

function HeatLegend() {
  const t = useTranslations('pages.admin.fleet-metrics');
  const swatch = (background: string, framed: boolean) => (
    <span
      style={{
        width: 12,
        height: 12,
        borderRadius: 2,
        background,
        border: framed ? '1px solid var(--color-neutral-200)' : 'none',
        flex: 'none',
        display: 'inline-block',
      }}
    />
  );

  return (
    <span className="inline-flex items-center text-xs text-text-tertiary" style={{ gap: 'var(--space-3)' }}>
      <span className="inline-flex items-center" style={{ gap: 4 }}>
        {swatch('var(--bg-subtle)', true)}
        {t('heat-none')}
      </span>
      <span className="inline-flex items-center" style={{ gap: 2 }}>
        {t('heat-low')}
        <span style={{ width: 4 }} />
        {HEAT_STEPS.map((step) => (
          <span key={step}>{swatch(step, false)}</span>
        ))}
        <span style={{ width: 4 }} />
        {t('heat-high')}
      </span>
    </span>
  );
}

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

/**
 * Hour × weekday per machine. One hue, four steps; a cell without a run is
 * `bg-subtle`, not the lightest step — nothing and little are different
 * statements. A cell whose runs are all unreadable gets a frame instead.
 */
export function HeatCard({
  heatmap,
  choice,
  compact,
}: {
  readonly heatmap: NonNullable<FleetMetricsSeries['heatmap']>;
  readonly choice: ClassChoice;
  readonly compact: boolean;
}) {
  const t = useTranslations('pages.admin.fleet-metrics');
  const max = heatmap.reduce(
    (outer, entry) =>
      entry.cells.reduce(
        (middle, row) => row.reduce((inner, cell) => Math.max(inner, classValue(cell, choice)), middle),
        outer,
      ),
    0,
  );
  const step = (value: number) =>
    value <= 0
      ? null
      : HEAT_STEPS[Math.min(HEAT_STEPS.length - 1, Math.floor((value / (max || 1)) * HEAT_STEPS.length))];
  const cell = compact ? 9 : 11;

  return (
    <MetricCard
      title={t('heat-title')}
      hint={compact ? null : t('heat-hint')}
      actions={compact ? null : <HeatLegend />}
      testId="heat-card"
    >
      <div className="flex flex-wrap" style={{ gap: 'var(--space-6)' }}>
        {heatmap.map((entry) => (
          <div
            key={entry.key}
            data-testid={`heat-${entry.key}`}
            className="flex flex-col"
            style={{ gap: 'var(--space-1-5)' }}
          >
            <Mono size="var(--text-sm)" weight="var(--font-weight-medium)">
              {entry.key}
            </Mono>
            {WEEKDAYS.map((weekday) => (
              <div key={weekday} className="flex items-center" style={{ gap: 2 }}>
                <span className="text-xs text-text-tertiary" style={{ width: 20 }}>
                  {t(`weekday-${weekday}`)}
                </span>
                {HOURS.map((hour) => {
                  const aggregate = entry.cells[weekday - 1]?.[hour] ?? null;
                  const background = step(classValue(aggregate, choice));

                  return (
                    <span
                      key={hour}
                      title={t('heat-cell', {
                        weekday: t(`weekday-${weekday}`),
                        hour: String(hour).padStart(2, '0'),
                        runs: aggregate?.runs ?? 0,
                      })}
                      style={{
                        width: cell,
                        height: cell,
                        borderRadius: 2,
                        flex: 'none',
                        background: background ?? 'var(--bg-subtle)',
                        boxShadow:
                          background === null && aggregate !== null && aggregate.runs > 0
                            ? 'inset 0 0 0 1px var(--color-neutral-400)'
                            : 'none',
                      }}
                    />
                  );
                })}
              </div>
            ))}
            <div className="flex" style={{ gap: 2, paddingLeft: 22 }}>
              {HOURS.map((hour) => (
                <span
                  key={hour}
                  style={{
                    ...MONO_STYLE,
                    width: cell,
                    flex: 'none',
                    fontSize: '10px',
                    color: 'var(--text-placeholder)',
                    overflow: 'visible',
                  }}
                >
                  {hour % 6 === 0 ? hour : ''}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
      {compact && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <HeatLegend />
        </div>
      )}
    </MetricCard>
  );
}

const SPLIT_TITLE: Readonly<Record<MetricsView, string>> = {
  machine: 'split-machine',
  agent: 'split-agent',
  model: 'split-model',
};

const PANEL_COLUMN: Readonly<Record<MetricsView, string>> = {
  machine: 'column-machine',
  agent: 'column-agent',
  model: 'column-model',
};

function SortHead<TField extends string>({
  field,
  text,
  label,
  style,
  sort,
  onSort,
}: {
  readonly field: TField;
  readonly text: string;
  readonly label?: ReactNode;
  readonly style: CSSProperties;
  readonly sort: SortState<TField>;
  readonly onSort: (field: TField) => void;
}) {
  const tTable = useTranslations('pages.admin.shell.table');

  return (
    <TableHead
      style={style}
      {...(sort !== null && sort.field === field ? { 'aria-sort': ARIA_SORT[sort.direction] } : {})}
    >
      <SortableHeader
        field={field}
        // Preflight sets `text-transform: none` on <button>; carry the head's own case onto the label.
        label={<span style={{ textTransform: style.textTransform }}>{label ?? text}</span>}
        ariaLabel={tTable('sort-label', { column: text })}
        sort={sort}
        onSort={onSort}
        align={style.textAlign === 'right' ? 'right' : 'left'}
      />
    </TableHead>
  );
}

export type SplitField = 'key' | 'sum' | TokenClass | 'shape';

type SplitRow = {
  readonly row: SeriesRow;
  readonly tokens: ReturnType<typeof tokensByClass>;
  readonly sum: number;
  readonly unknown: boolean;
  readonly shape: number | null;
};

function splitSortKey({ row, tokens, sum, unknown, shape }: SplitRow, field: SplitField): SortValue {
  switch (field) {
    case 'key':
      return row.key;
    case 'sum':
      return unknown ? null : sum;
    case 'shape':
      return shape;
    default:
      return unknown ? null : tokens[field];
  }
}

/**
 * The four classes side by side per panel group. Each class keeps its own
 * order of magnitude — a shared axis would make output and uncached input
 * invisible — so the table carries the numbers, the stack the shape.
 */
export function SplitCard({
  view,
  rows,
  sortState,
}: {
  readonly view: MetricsView;
  readonly rows: ReadonlyArray<SeriesRow>;
  readonly sortState: SortStateHandle<SplitField>;
}) {
  const t = useTranslations('pages.admin.fleet-metrics');
  const format = useMetricFormat();
  const splitRows = useMemo(
    () =>
      rows.map((row): SplitRow => {
        const tokens = tokensByClass(row.total);
        const sum = classValue(row.total, 'all');
        const unknown = isUnknown(row.total);

        return { row, tokens, sum, unknown, shape: unknown ? null : sum > 0 ? tokens[TOKEN_CLASSES[0]] / sum : 0 };
      }),
    [rows],
  );
  const { sorted, sort, toggleSort } = useTableSort(splitRows, splitSortKey, sortState);
  // A zero under cache write is measured, not missing — said once per model that shows one.
  const noCacheWrite =
    view === 'model'
      ? rows.filter((row) => parsedRuns(row.total) > 0 && row.total.cacheWriteInputTokens === 0).map((row) => row.key)
      : [];

  return (
    <MetricCard title={t(SPLIT_TITLE[view])} hint={t('split-hint')} testId="split-card">
      <Table scrollX className="min-w-[760px]" style={{ tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: 150 }} />
          <col style={{ width: 110 }} />
          <col style={{ width: 124 }} />
          <col style={{ width: 148 }} />
          <col style={{ width: 132 }} />
          <col style={{ width: 112 }} />
          <col />
        </colgroup>
        <TableHeader>
          <TableRow>
            <SortHead field="key" text={t(PANEL_COLUMN[view])} style={HEADER_STYLE} sort={sort} onSort={toggleSort} />
            <SortHead
              field="sum"
              text={t('sum')}
              style={{ ...HEADER_STYLE, ...RIGHT }}
              sort={sort}
              onSort={toggleSort}
            />
            {TOKEN_CLASSES.map((key) => (
              <SortHead
                key={key}
                field={key}
                text={TOKEN_CLASS_LABEL[key]}
                label={
                  <span className="inline-flex items-center" style={{ gap: 6 }}>
                    <ClassSwatch tokenClass={key} size={8} />
                    {TOKEN_CLASS_LABEL[key]}
                  </span>
                }
                style={{ ...CLASS_HEADER_STYLE, ...RIGHT }}
                sort={sort}
                onSort={toggleSort}
              />
            ))}
            <SortHead field="shape" text={t('split-shape')} style={HEADER_STYLE} sort={sort} onSort={toggleSort} />
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map(({ row, tokens, sum, unknown }) => {
            return (
              <TableRow key={row.key} data-testid={`split-row-${row.key}`}>
                <TableCell className="truncate" title={row.key}>
                  <Mono>{row.key}</Mono>
                </TableCell>
                <TableCell style={RIGHT}>
                  <Approx show={isApproximate(row.total) && !unknown} title={t('approx-title')} />
                  <Mono weight="var(--font-weight-medium)">{unknown ? '—' : format.tokensShort(sum)}</Mono>
                </TableCell>
                {TOKEN_CLASSES.map((key) => (
                  <TableCell key={key} style={RIGHT}>
                    <div className="flex flex-col items-end" style={{ lineHeight: 1.25 }}>
                      <Mono size="var(--text-sm)" color="var(--text-secondary)">
                        {unknown ? '—' : format.tokensShort(tokens[key])}
                      </Mono>
                      {!unknown && (
                        <Mono size="10px" color="var(--text-placeholder)">
                          {format.percent(sum > 0 ? tokens[key] / sum : 0)}
                        </Mono>
                      )}
                    </div>
                  </TableCell>
                ))}
                <TableCell>{!unknown && <ClassBar values={tokens} height={12} labels={false} />}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {noCacheWrite.map((model) => (
        <p key={model} className="text-sm text-text-tertiary" style={{ margin: 'var(--space-3) 0 0' }}>
          {t('no-cache-write', { model })}
        </p>
      ))}
    </MetricCard>
  );
}

export type LeaderField =
  'rank' | 'agent' | 'tokens' | 'share' | 'delta' | 'runs' | 'perRun' | 'outputPerRun' | 'runtime' | 'cost';

type LeaderRow = {
  readonly row: SeriesRow;
  readonly value: number;
  readonly unknown: boolean;
  readonly approximate: boolean;
  readonly parsed: number;
  readonly delta: Delta | null;
  readonly sortValues: Readonly<Record<LeaderField, SortValue>>;
};

function leaderSortKey(row: LeaderRow, field: LeaderField): SortValue {
  return row.sortValues[field];
}

/** The agents, ranked by the chosen class — the one card of the agent view. */
export function LeaderCard({
  rows,
  choice,
  costs,
  hasPrevious,
  sortState,
}: {
  readonly rows: ReadonlyArray<SeriesRow>;
  readonly choice: ClassChoice;
  readonly costs: ReadonlyMap<string, number>;
  readonly hasPrevious: boolean;
  readonly sortState: SortStateHandle<LeaderField>;
}) {
  const t = useTranslations('pages.admin.fleet-metrics');
  const format = useMetricFormat();
  const { leaderRows, top } = useMemo(() => {
    const ranked = [...rows]
      .map((row) => ({ row, value: classValue(row.total, choice) }))
      .sort((a, b) => b.value - a.value);
    const sum = ranked.reduce((total, entry) => total + entry.value, 0);

    return {
      top: ranked[0]?.value ?? 0,
      leaderRows: ranked.map(({ row, value }, index): LeaderRow => {
        const parsed = parsedRuns(row.total);
        const unknown = isUnknown(row.total);
        const change =
          hasPrevious && row.previous !== null && !unknown ? delta(value, classValue(row.previous, choice)) : null;
        const share = sum > 0 ? value / sum : 0;

        return {
          row,
          value,
          unknown,
          approximate: isApproximate(row.total) && !unknown,
          parsed,
          delta: change,
          sortValues: {
            rank: index + 1,
            agent: row.key,
            tokens: unknown ? null : value,
            share,
            // „neu" is growth from nothing: above every percentage, and equal to another „neu".
            delta: change === null ? null : change.kind === 'new' ? Number.MAX_VALUE : change.value,
            runs: row.total.runs,
            perRun: parsed === 0 ? null : Math.round(value / parsed),
            outputPerRun: parsed === 0 ? null : Math.round(row.total.outputTokens / parsed),
            runtime: row.total.runs === 0 ? null : Math.round(row.total.durationMs / row.total.runs),
            cost: costs.get(row.key) ?? 0,
          },
        };
      }),
    };
  }, [rows, choice, costs, hasPrevious]);
  const { sorted, sort, toggleSort } = useTableSort(leaderRows, leaderSortKey, sortState);
  const sum = leaderRows.reduce((total, entry) => total + entry.value, 0);

  return (
    <MetricCard title={t('leader-title')} hint={t('leader-hint')} testId="leader-card">
      <Table scrollX className="min-w-[960px]" style={{ tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: 28 }} />
          <col style={{ width: 118 }} />
          <col style={{ width: 104 }} />
          <col />
          <col style={{ width: 140 }} />
          <col style={{ width: 64 }} />
          <col style={{ width: 88 }} />
          <col style={{ width: 124 }} />
          <col style={{ width: 88 }} />
          <col style={{ width: 84 }} />
        </colgroup>
        <TableHeader>
          <TableRow>
            <SortHead field="rank" text={'#'} style={HEADER_STYLE} sort={sort} onSort={toggleSort} />
            <SortHead field="agent" text={t('column-agent')} style={HEADER_STYLE} sort={sort} onSort={toggleSort} />
            <SortHead
              field="tokens"
              text={t('leader-tokens')}
              style={{ ...HEADER_STYLE, ...RIGHT }}
              sort={sort}
              onSort={toggleSort}
            />
            <SortHead field="share" text={t('leader-share')} style={HEADER_STYLE} sort={sort} onSort={toggleSort} />
            <SortHead
              field="delta"
              text={t('leader-delta')}
              style={{ ...HEADER_STYLE, ...RIGHT }}
              sort={sort}
              onSort={toggleSort}
            />
            <SortHead
              field="runs"
              text={t('leader-runs')}
              style={{ ...HEADER_STYLE, ...RIGHT }}
              sort={sort}
              onSort={toggleSort}
            />
            <SortHead
              field="perRun"
              text={t('leader-per-run')}
              style={{ ...HEADER_STYLE, ...RIGHT }}
              sort={sort}
              onSort={toggleSort}
            />
            <SortHead
              field="outputPerRun"
              text={t('leader-output-per-run')}
              style={{ ...HEADER_STYLE, ...RIGHT }}
              sort={sort}
              onSort={toggleSort}
            />
            <SortHead
              field="runtime"
              text={t('leader-runtime')}
              style={{ ...HEADER_STYLE, ...RIGHT }}
              sort={sort}
              onSort={toggleSort}
            />
            <SortHead
              field="cost"
              text={t('leader-cost')}
              style={{ ...HEADER_STYLE, ...RIGHT }}
              sort={sort}
              onSort={toggleSort}
            />
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map(({ row, value, unknown, approximate, parsed, delta: change, sortValues }) => {
            return (
              <TableRow key={row.key} data-testid={`leader-row-${row.key}`}>
                <TableCell>
                  <Mono color="var(--text-placeholder)">{sortValues.rank}</Mono>
                </TableCell>
                <TableCell className="truncate" title={row.key}>
                  <Mono weight="var(--font-weight-medium)">{row.key}</Mono>
                </TableCell>
                <TableCell style={RIGHT}>
                  <Approx show={approximate} title={t('approx-title')} />
                  <Mono>{unknown ? '—' : format.tokensShort(value)}</Mono>
                </TableCell>
                <TableCell>
                  <span className="flex items-center" style={{ gap: 'var(--space-2)' }}>
                    <span
                      style={{
                        flex: 1,
                        height: 8,
                        background: 'var(--color-neutral-100)',
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}
                    >
                      <span
                        style={{
                          display: 'block',
                          height: '100%',
                          width: `${top > 0 ? (value / top) * 100 : 0}%`,
                          background: 'var(--color-info-500)',
                          borderRadius: '0 2px 2px 0',
                        }}
                      />
                    </span>
                    <Mono size="var(--text-sm)" color="var(--text-tertiary)">
                      {format.percentWhole(sum > 0 ? value / sum : 0)}
                    </Mono>
                  </span>
                </TableCell>
                <TableCell style={RIGHT}>
                  <Mono size="var(--text-sm)" color="var(--text-secondary)">
                    {change === null ? '—' : format.delta(change)}
                  </Mono>
                </TableCell>
                <TableCell style={RIGHT}>
                  <Mono color="var(--text-secondary)">{format.count(row.total.runs)}</Mono>
                </TableCell>
                <TableCell style={RIGHT}>
                  <Mono size="var(--text-sm)" color="var(--text-secondary)">
                    {parsed > 0 ? format.tokensShort(Math.round(value / parsed)) : '—'}
                  </Mono>
                </TableCell>
                <TableCell style={RIGHT}>
                  <Mono size="var(--text-sm)" color="var(--text-secondary)">
                    {parsed > 0 ? format.tokensShort(Math.round(row.total.outputTokens / parsed)) : '—'}
                  </Mono>
                </TableCell>
                <TableCell style={RIGHT}>
                  <Mono size="var(--text-sm)" color="var(--text-tertiary)">
                    {row.total.runs > 0 ? format.duration(Math.round(row.total.durationMs / row.total.runs)) : '—'}
                  </Mono>
                </TableCell>
                <TableCell style={RIGHT}>
                  <Approx show={isApproximate(row.total)} title={t('approx-title')} />
                  <Mono size="var(--text-sm)" color="var(--text-tertiary)">
                    {format.eur(costs.get(row.key) ?? 0)}
                  </Mono>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </MetricCard>
  );
}

export type MixField = 'agent' | `model:${string}`;

type MixRow = {
  readonly profile: string;
  readonly byModel: ReadonlyMap<string, FleetMetricAggregate>;
  readonly rowTotal: number;
  readonly shares: ReadonlyMap<string, number | null>;
};

function mixSortKey(row: MixRow, field: MixField): SortValue {
  if (field === 'agent') return row.profile;

  return row.shares.get(field.slice('model:'.length)) ?? null;
}

/** Agents × models: each model's share of the agent's tokens — the one card of the model view. */
export function MixCard({
  models,
  cells,
  choice,
  sortState,
}: {
  readonly models: ReadonlyArray<string>;
  /** profile → model → aggregate. */
  readonly cells: ReadonlyMap<string, ReadonlyMap<string, FleetMetricAggregate>>;
  readonly choice: ClassChoice;
  readonly sortState: SortStateHandle<MixField>;
}) {
  const t = useTranslations('pages.admin.fleet-metrics');
  const format = useMetricFormat();
  const mixRows = useMemo(
    () =>
      [...cells.entries()].map(([profile, byModel]): MixRow => {
        const rowTotal = [...byModel.values()].reduce((sum, aggregate) => sum + classValue(aggregate, choice), 0);
        const shares = new Map<string, number | null>();

        for (const model of models) {
          const aggregate = byModel.get(model);

          shares.set(
            model,
            aggregate === undefined || isUnknown(aggregate)
              ? null
              : rowTotal > 0
                ? classValue(aggregate, choice) / rowTotal
                : 0,
          );
        }

        return { profile, byModel, rowTotal, shares };
      }),
    [cells, models, choice],
  );
  const { sorted, sort, toggleSort } = useTableSort(mixRows, mixSortKey, sortState);

  return (
    <MetricCard title={t('mix-title')} hint={t('mix-hint')} testId="mix-card">
      <Table scrollX style={{ tableLayout: 'fixed', minWidth: 150 + models.length * 260 }}>
        <colgroup>
          <col style={{ width: 150 }} />
          {models.map((model) => (
            <col key={model} />
          ))}
        </colgroup>
        <TableHeader>
          <TableRow>
            <SortHead field="agent" text={t('column-agent')} style={HEADER_STYLE} sort={sort} onSort={toggleSort} />
            {models.map((model) => (
              <SortHead
                key={model}
                field={`model:${model}`}
                text={model}
                style={CLASS_HEADER_STYLE}
                sort={sort}
                onSort={toggleSort}
              />
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map(({ profile, byModel, rowTotal }) => {
            return (
              <TableRow key={profile} data-testid={`mix-row-${profile}`}>
                <TableCell className="truncate" title={profile}>
                  <Mono>{profile}</Mono>
                </TableCell>
                {models.map((model) => {
                  const aggregate = byModel.get(model);

                  if (aggregate === undefined) return <TableCell key={model} />;

                  const unknown = isUnknown(aggregate);
                  const value = classValue(aggregate, choice);
                  const share = rowTotal > 0 ? value / rowTotal : 0;

                  return (
                    <TableCell key={model}>
                      <div className="flex items-center" style={{ gap: 'var(--space-2)' }}>
                        <span
                          style={{
                            width: 96,
                            height: 8,
                            background: 'var(--color-neutral-100)',
                            borderRadius: 2,
                            overflow: 'hidden',
                            flex: 'none',
                          }}
                        >
                          <span
                            style={{
                              display: 'block',
                              height: '100%',
                              width: `${share * 100}%`,
                              background: 'var(--color-info-500)',
                            }}
                          />
                        </span>
                        <Mono size="var(--text-sm)" color="var(--text-secondary)">
                          {unknown ? '—' : format.percentWhole(share)}
                        </Mono>
                        <span className="flex-1" />
                        <span style={{ whiteSpace: 'nowrap' }}>
                          <Approx show={isApproximate(aggregate) && !unknown} title={t('approx-title')} />
                          <Mono size="var(--text-xs)" color="var(--text-tertiary)">
                            {unknown ? '—' : format.tokensShort(value)}
                          </Mono>
                        </span>
                        <Mono size="var(--text-xs)" color="var(--text-placeholder)">
                          {t('mix-runs', { count: aggregate.runs })}
                        </Mono>
                      </div>
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </MetricCard>
  );
}

/** Resolves the `≈` and the dashed column — only when a run without readable usage is in the period — then the price note. */
export function MetricsFooter({
  total,
  unpricedModels,
}: {
  readonly total: FleetMetricAggregate;
  readonly unpricedModels: ReadonlyArray<string>;
}) {
  const t = useTranslations('pages.admin.fleet-metrics');
  const intl = useIntl();

  return (
    <div className="flex flex-col" style={{ gap: 'var(--space-2)' }}>
      {total.unparsedRuns > 0 && (
        <div
          data-testid="unparsed-note"
          className="flex items-start"
          style={{
            gap: 'var(--space-2)',
            padding: 'var(--space-3) var(--space-4)',
            background: 'var(--color-neutral-50)',
            border: '1px solid var(--color-neutral-200)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <span
            aria-hidden
            style={{ ...MONO_STYLE, fontSize: 'var(--text-md)', color: 'var(--text-tertiary)', lineHeight: 1.1 }}
          >
            ≈
          </span>
          <div className="flex flex-col" style={{ gap: 4 }}>
            <span className="text-text-secondary" style={{ fontSize: 'var(--text-base-sm)' }}>
              {t('unparsed-note', { count: total.unparsedRuns })}
            </span>
            <span className="inline-flex items-center text-sm text-text-tertiary" style={{ gap: 'var(--space-2)' }}>
              <span
                aria-hidden
                style={{
                  width: 12,
                  height: 12,
                  border: '1px dashed var(--color-neutral-400)',
                  borderBottom: 'none',
                  borderRadius: '3px 3px 0 0',
                }}
              />
              {t('unknown-legend')}
            </span>
          </div>
        </div>
      )}
      <span className="text-sm" style={{ color: 'var(--text-placeholder)' }} data-testid="pricing-footnote">
        {t('pricing-footnote', {
          asOf: intl.formatDate(`${FLEET_PRICING.asOf}T00:00:00Z`, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            timeZone: 'UTC',
          }),
          rate: intl.formatNumber(FLEET_PRICING.eurPerUsd, { minimumFractionDigits: 2 }),
        })}
        {unpricedModels.length > 0 &&
          ` ${t('pricing-fallback', { models: unpricedModels.join(', '), fallback: FLEET_PRICING.fallbackModel })}`}
      </span>
    </div>
  );
}

/**
 * The page's body while a filter change loads: the KPI row and the panels card
 * in outline, so a click reads as "working" rather than as nothing happening.
 */
export function MetricsSkeleton({ compact }: { readonly compact: boolean }) {
  const t = useTranslations('pages.admin.fleet-metrics');

  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={t('loading')}
      className="flex w-full min-w-0 flex-col"
      style={{ gap: 'var(--space-4)' }}
      data-testid="metrics-skeleton"
    >
      <Card
        className="grid min-w-0"
        style={{ gridTemplateColumns: compact ? '1fr 1fr' : '2.1fr 1fr 1fr 1fr 1fr', gap: 'var(--space-4)' }}
      >
        {Array.from({ length: compact ? 4 : 5 }, (_, index) => (
          <div key={index} className="flex flex-col" style={{ gap: 'var(--space-2)', padding: 'var(--space-4)' }}>
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-7 w-3/4" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        ))}
      </Card>
      <Card className="flex min-w-0 flex-col" style={{ gap: 'var(--space-3)', padding: 'var(--space-4)' }}>
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-48 w-full" />
        {Array.from({ length: 3 }, (_, index) => (
          <Skeleton key={index} className="h-4 w-full" />
        ))}
      </Card>
    </div>
  );
}
